import React, { FC, useState } from 'react';
import {
  PageContent,
  PageFooter,
  BlockedGuy,
  Input,
  MaskedInput,
  FormTemplate,
} from 'components';
import { useForm } from 'react-hook-form';
import { postRisk, validateFields } from 'services';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppContext } from 'hooks';
import { FuelButton, FuelDivider, FuelTypography } from '@frete.com/fuel-react';
import { useOutletContext } from 'react-router-dom';
import { RiskFlowControl } from 'types/RiskControl';
import { onInputName, onInputPhone } from './inputFunctions';
import { validationSchema } from './validationSchema';
import { Heading, InputList, ListItens } from './styled';

const RequesterInformation = {
  image: <BlockedGuy />,
  title: `Informe os dados do solicitante para validar a carga`,
};

const CargoInformation: FC = () => {
  const defaultValues = {
    socialEnterpriseName: '',
    enterpriseCnpj: '',
    responsibleName: '',
    responsiblePhone: '',
  };

  const context = useAppContext();
  const outletContext = useOutletContext<RiskFlowControl>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const makeSubmit = async (values: typeof defaultValues) => {
    const { riskFlow, setRiskFlow } = outletContext;

    try {
      const { freightRisk, freightId } = riskFlow.data;
      const { steps } = context.state;
      await postRisk({ freightId, freightRisk, ...values });

      context.dispatch({
        type: 'update_step',
        payload: { currentStep: steps.currentStep + 1 },
      });
    } catch (error) {
      setRiskFlow({
        ...riskFlow,
        hadSuccess: false,
        hadError: true,
        riskProcessed: true,
      });
    }
  };

  const submitFunction = async (values: typeof defaultValues) => {
    const { enterpriseCnpj, responsiblePhone } = values;

    setIsLoading(true);
    try {
      const { data } = await validateFields({
        enterpriseCnpj,
        responsiblePhone,
      });

      if (data?.errors?.enterpriseCnpj)
        setError('enterpriseCnpj', {
          type: 'manual',
          message: data.errors.enterpriseCnpj,
        });

      if (data?.errors?.responsiblePhone)
        setError('responsiblePhone', {
          type: 'manual',
          message: data?.errors?.responsiblePhone,
        });

      if (data?.errors?.enterpriseCnpj || data?.errors?.responsiblePhone)
        return;

      context.dispatch({
        type: 'set_requesterInformation',
        payload: {
          requesterInformation: { ...values },
        },
      });

      await makeSubmit(values);
    } catch (error) {
      setError('responsiblePhone', {
        type: 'manual',
        message: error?.response?.data?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousStep = () => {
    const { steps } = context.state;

    context.dispatch({
      type: 'update_step',
      payload: { currentStep: steps.currentStep - 1 },
    });
  };

  const hasError = Object.values(errors ?? {}).some((value) => !!value);

  return (
    <FormTemplate onSubmit={handleSubmit(submitFunction)}>
      <Heading />
      <PageContent content={RequesterInformation}>
        <InputList>
          <ListItens>
            <Input
              name="responsibleName"
              id="responsibleName"
              label="Nome do negociante"
              placeHolder="Nome do negociante"
              error={!!errors.responsibleName}
              errorMessage={errors?.responsibleName?.message}
              register={register}
              onInput={onInputName}
            />
            <Input
              name="responsiblePhone"
              id="responsiblePhone"
              label="Contato do negociante"
              placeHolder="(00) 00000-0000"
              error={!!errors.responsiblePhone}
              errorMessage={errors?.responsiblePhone?.message}
              register={register}
              type="tel"
              onInput={onInputPhone}
            />
          </ListItens>
          <Input
            name="socialEnterpriseName"
            id="socialEnterpriseName"
            label="Razão social da empresa responsável"
            placeHolder="Digite o nome da empresa"
            error={!!errors.socialEnterpriseName}
            errorMessage={errors?.socialEnterpriseName?.message}
            register={register}
            onInput={onInputName}
          />
          <MaskedInput
            mask="99.999.999/9999-99"
            name="enterpriseCnpj"
            id="enterpriseCnpj"
            label="CNPJ da empresa responsável"
            placeHolder="00.000.000/0000-00"
            error={!!errors.enterpriseCnpj}
            errorMessage={errors?.enterpriseCnpj?.message}
            register={register}
          />
          <FuelTypography
            variant="regular"
            tag="p"
            size="caption"
            displayInline
          >
            Utilizamos os dados para confirmar a propriedade do frete durante
            nossa análise e não compartilharemos com terceiros.
          </FuelTypography>
        </InputList>
      </PageContent>
      <PageFooter disableStepsSwitcher isLoading={isLoading}>
        <FuelDivider />
        <FuelButton
          type="button"
          variant="tertiary"
          size="medium"
          onClick={handlePreviousStep}
          disabled={isLoading}
        >
          Voltar
        </FuelButton>
        <FuelButton
          type="submit"
          variant="primary"
          size="medium"
          trailingIcon="arrow-right"
          disabled={hasError}
          isLoading={isLoading}
        >
          Avançar
        </FuelButton>
      </PageFooter>
    </FormTemplate>
  );
};

export default CargoInformation;
